import React, { useEffect, useState } from "react";
import moment from "moment";
import { Button, Modal, notification, Spin } from "antd";
import { companyService, legalEntityService } from "../../../services";
import GuarantorFields from "../create-legal-entity/belarus-legal-entity/steps/guarantor-fields";
import i18n from "../../../i18n";


const getErrorDescription = (error) => {
  if (typeof error === "string") {
    return error;
  }
  if (error && typeof error.message === "string") {
    return error.message;
  }
  return String(error || "");
};

export default function BelarusEditGuarantor({
  isShow,
  setIsShow,
  legalEntityId,
  onSaved,
}) {
  const [loading, setLoading] = useState(false);
  const [fieldsErrors, setFieldsErrors] = useState({});
  const [legalEntityData, setLegalEntityData] = useState(null);
  const [guarantorFields, setGuarantorFields] = useState({
    guarantorFirstName: "",
    guarantorSecondName: "",
    guarantorPatronymic: "",
    guarantorPassportSerial: "",
    guarantorPassportNumber: "",
    guarantorPassportDate: null,
    guarantorPassportRegistration: "",
    guarantorAddressFact: "",
    guarantorAddressRegistration: "",
    guarantorCountry: i18n.t("constants.countries.2"),
    guarantorPhone: "",
    guarantorEmail: "",
  });

  const getFieldErrors = (fieldName) => {
    if (!fieldsErrors || typeof fieldsErrors[fieldName] === "undefined") {
      return null;
    }
    const fieldError = fieldsErrors[fieldName];
    if (Array.isArray(fieldError)) {
      return fieldError.join("; ");
    }
    return fieldError || null;
  };

  useEffect(() => {
    if (!isShow || !legalEntityId) {
      return;
    }

    let isMounted = true;
    setLoading(true);
    setFieldsErrors({});

    companyService.getLegalEntityData(legalEntityId)
      .then((data) => {
        if (!isMounted) {
          return;
        }

        setLegalEntityData(data);
        setGuarantorFields({
          guarantorFirstName: data.GuarantorFirstName || "",
          guarantorSecondName: data.GuarantorSecondName || "",
          guarantorPatronymic: data.GuarantorPatronymic || "",
          guarantorPassportSerial: data.GuarantorPassportSerial || "",
          guarantorPassportNumber: data.GuarantorPassportNumber || "",
          guarantorPassportDate: data.GuarantorPassportDate
            ? moment(data.GuarantorPassportDate, "DD.MM.YYYY")
            : null,
          guarantorPassportRegistration: data.GuarantorPassportRegistration || "",
          guarantorAddressFact: data.GuarantorAddressFact || "",
          guarantorAddressRegistration: data.GuarantorAddressRegistration || "",
          guarantorCountry: data.GuarantorCountry || i18n.t("constants.countries.2"),
          guarantorPhone: data.GuarantorPhone || "",
          guarantorEmail: data.GuarantorEmail || "",
        });
      })
      .catch((error) => {
        notification.error({
          message: i18n.t("components.notification.titles.editLegalEntityData"),
          description: getErrorDescription(error),
        });
      })
      .finally(() => {
        if (isMounted) {
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isShow, legalEntityId]);

  const handleClose = () => {
    setFieldsErrors({});
    setIsShow(false);
  };

  const handleSave = () => {
    if (!legalEntityData || !legalEntityId) {
      return;
    }

    setLoading(true);

    const payload = {
      orgFormId: legalEntityData.OrgFormId,
      countryId: legalEntityData.CountryId,
      workWithNds: legalEntityData.WorkWithNds,
      name: legalEntityData.Name,
      fullName: legalEntityData.FullName,
      firstName: legalEntityData.FirstName,
      secondName: legalEntityData.SecondName,
      patronymic: legalEntityData.Patronymic,
      email: legalEntityData.Email,
      phone: legalEntityData.Phone,
      bankName: legalEntityData.BankName,
      bankAddress: legalEntityData.BankAddress,
      accountNumber: legalEntityData.AccountNumber,
      swift: legalEntityData.Swift,
      bankUnp: legalEntityData.BankUnp,
      bic: legalEntityData.Bic,
      correspondentBankName: legalEntityData.CorrespondentBankName || "",
      correspondentBankInn: legalEntityData.CorrespondentBankInn || "",
      correspondentBankBic: legalEntityData.CorrespondentBankBic || "",
      correspondentBankLegalAddress: legalEntityData.CorrespondentBankLegalAddress || "",
      correspondentBankAccount: legalEntityData.CorrespondentBankAccount || "",
      correspondentBankCheckingAccount: legalEntityData.CorrespondentBankCheckingAccount || "",
      position: legalEntityData.Position,
      legalNumber: legalEntityData.LegalNumber,
      legalReason: legalEntityData.LegalReason,
      atiCode: legalEntityData.AtiCode,
      addressCity: legalEntityData.AddressCity,
      address: legalEntityData.Address,
      mailingAddress: legalEntityData.MailingAddress,
      bin: legalEntityData.Bin,
      dateRegistration: legalEntityData.DateRegistration
        ? (moment.isMoment(legalEntityData.DateRegistration)
            ? legalEntityData.DateRegistration.format("DD.MM.YYYY")
            : (typeof legalEntityData.DateRegistration === "string" 
                ? legalEntityData.DateRegistration 
                : ""))
        : "",
      iik: legalEntityData.Iik,
      rs: legalEntityData.Rs,
      addGuarantor: true,
      guarantorFirstName: guarantorFields.guarantorFirstName,
      guarantorSecondName: guarantorFields.guarantorSecondName,
      guarantorPatronymic: guarantorFields.guarantorPatronymic,
      guarantorPassportSerial: guarantorFields.guarantorPassportSerial,
      guarantorPassportNumber: guarantorFields.guarantorPassportNumber,
      guarantorPassportDate: guarantorFields.guarantorPassportDate
        ? guarantorFields.guarantorPassportDate.format("DD.MM.YYYY")
        : "",
      guarantorPassportRegistration: guarantorFields.guarantorPassportRegistration,
      guarantorAddressFact: guarantorFields.guarantorAddressFact,
      guarantorAddressRegistration: guarantorFields.guarantorAddressRegistration,
      guarantorCountry: guarantorFields.guarantorCountry,
      guarantorPhone: guarantorFields.guarantorPhone,
      guarantorEmail: guarantorFields.guarantorEmail,
      
    };

    const savePromise = (legalEntityData.CountryId === 6)
      ? legalEntityService.editKazakhstanLegalEntity(legalEntityId, payload, legalEntityData.OrgFormId)
      : companyService.editBelarusLegalEntity(payload, legalEntityId);

    savePromise
      .then((response) => {
        if (response.code === 400 || response.status === "error") {
          setFieldsErrors(response.errors || {});
          notification.error({
            message: i18n.t("components.notification.titles.editLegalEntityData"),
            description: response.message,
          });
          return;
        }

        notification.success({
          message: i18n.t("components.notification.titles.editLegalEntityData"),
          description: response.message,
        });

        handleClose();
        if (typeof onSaved === "function") {
          onSaved();
        }
      })
      .catch((error) => {
        notification.error({
          message: i18n.t("components.notification.titles.editLegalEntityData"),
          description: getErrorDescription(error),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      title={i18n.t("modals.modalEditLegalEntity.divider.guarantor")}
      visible={isShow}
      onCancel={handleClose}
      width={1100}
      footer={[
        <Button key="close" onClick={handleClose}>
          {i18n.t("main.buttons.close")}
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} disabled={loading || !legalEntityId}>
          {i18n.t("main.buttons.save")}
        </Button>,
      ]}
    >
      <Spin spinning={loading} tip={i18n.t("main.loadText.loading")}>
        <GuarantorFields
          stepFields={guarantorFields}
          setFields={setGuarantorFields}
          getFieldErrors={getFieldErrors}
          edit={true}
        />
      </Spin>
    </Modal>
  );
}
